import React, { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import axios from 'axios'
import DateRangeSelector, { Range } from '../components/DateRangeFilter'
import { format, subDays, differenceInDays } from 'date-fns'
import { getServiceAbbreviation } from '../lib/serviceAbbreviations'
import { EmptyState } from '../components/EmptyState'
import { TrendChart } from '../components/TrendChart'

interface ReportData {
  period: string
  totalCost: number
  breakdown: Array<{
    [key: string]: any
    cost: number
    percentage: number
  }>
  dailyCosts: Array<{
    date: string
    cost: number
  }>
}

function csvEscape(value: string | number): string {
  const s = String(value)
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export default function ReportsPage() {
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<Range>({
    start: subDays(new Date(), 29),
    end: new Date(),
    preset: 30
  })
  const [reportConfig, setReportConfig] = useState({
    groupBy: 'service',
    format: 'chart'
  })

  useEffect(() => {
    generateReport()
  }, [dateRange, reportConfig])

  const generateReport = async () => {
    try {
      setLoadError(null)
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const startStr = format(dateRange.start, 'yyyy-MM-dd')
      const endStr = format(dateRange.end, 'yyyy-MM-dd')

      const daysDiff = differenceInDays(dateRange.end, dateRange.start) + 1

      const [summaryRes, breakdownRes] = await Promise.all([
        axios.get(`/api/costs/summary?start=${startStr}&end=${endStr}`, { headers }),
        axios.get(
          `/api/costs/breakdown?start=${startStr}&end=${endStr}&groupBy=${reportConfig.groupBy}`,
          { headers }
        )
      ])

      setReportData({
        period: `${daysDiff} days`,
        totalCost: summaryRes.data.summary?.totalCost || 0,
        breakdown: breakdownRes.data.breakdown || [],
        dailyCosts: summaryRes.data.dailyCosts || []
      })
    } catch (error) {
      console.error('Failed to generate report:', error)
      setReportData(null)
      const msg =
        axios.isAxiosError(error) && error.response?.data?.error
          ? String(error.response.data.error)
          : 'Failed to load report. Please try again.'
      setLoadError(msg)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value)
  }

  const exportReport = (format: 'csv') => {
    if (!reportData) return

    if (format === 'csv') {
      const columnName =
        reportConfig.groupBy === 'service'
          ? 'Service'
          : reportConfig.groupBy === 'account'
            ? 'Account'
            : 'Region'

      const csvData = reportData.breakdown.map((item) => ({
        [columnName]:
          reportConfig.groupBy === 'service'
            ? item.service
            : reportConfig.groupBy === 'account'
              ? item.accountName || item.accountId
              : item.region,
        Cost: item.cost,
        Percentage: Number(item.percentage ?? 0).toFixed(2)
      }))

      const csvContent = [
        [columnName, 'Cost', 'Percentage'],
        ...csvData.map((row) => [
          csvEscape(row[columnName] as string),
          csvEscape(row.Cost),
          csvEscape(row.Percentage)
        ])
      ]
        .map((row) => row.join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `finops-report-${reportConfig.groupBy}-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <p className="ml-4 text-gray-600">Generating report...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-600">Generate detailed cost analysis reports</p>
        </div>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Report Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Group By</label>
            <select
              value={reportConfig.groupBy}
              onChange={(e) => setReportConfig((prev) => ({ ...prev, groupBy: e.target.value }))}
              className="form-input"
            >
              <option value="service">Service</option>
              <option value="account">Account</option>
              <option value="region">Region</option>
            </select>
          </div>

          <div>
            <label className="form-label">View Format</label>
            <select
              value={reportConfig.format}
              onChange={(e) => setReportConfig((prev) => ({ ...prev, format: e.target.value }))}
              className="form-input"
            >
              <option value="chart">Charts</option>
              <option value="table">Table</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {loadError}
        </div>
      )}

      {/* Report Summary */}
      {reportData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Report Period</p>
                  <p className="text-2xl font-bold text-gray-900">{reportData.period}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Cost</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.totalCost)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {reportConfig.groupBy === 'service'
                      ? 'Services'
                      : reportConfig.groupBy === 'account'
                        ? 'Accounts'
                        : 'Regions'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{reportData.breakdown.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Export</p>
                    <p className="text-xs text-gray-400">Download report</p>
                  </div>
                </div>
                <button
                  onClick={() => exportReport('csv')}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  CSV
                </button>
              </div>
            </div>
          </div>

          {/* Cost Trend Chart */}
          {(reportConfig.format === 'chart' || reportConfig.format === 'both') && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              {reportData.dailyCosts.length > 0 ? (
                <TrendChart
                  data={reportData.dailyCosts}
                  dataKey="cost"
                  xAxisKey="date"
                  title="Cost Trend Over Time"
                  color="#2563eb"
                  height={320}
                  valueFormatter={formatCurrency}
                  defaultChartType="line"
                  enableToggle={true}
                  dateRange={dateRange}
                />
              ) : (
                <EmptyState
                  icon="chart"
                  title="No daily cost data available"
                  description="Daily cost trend data will appear here once available."
                  className="h-64"
                />
              )}
            </div>
          )}

          {/* Breakdown Chart */}
          {(reportConfig.format === 'chart' || reportConfig.format === 'both') && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Top{' '}
                {reportConfig.groupBy === 'service'
                  ? 'Services'
                  : reportConfig.groupBy === 'account'
                    ? 'Accounts'
                    : 'Regions'}{' '}
                by Cost
              </h3>
              {reportData.breakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={reportData.breakdown.slice(0, 15)}
                    margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey={
                        reportConfig.groupBy === 'service'
                          ? 'service'
                          : reportConfig.groupBy === 'account'
                            ? 'accountName'
                            : 'region'
                      }
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tickFormatter={(value) =>
                        reportConfig.groupBy === 'service' ? getServiceAbbreviation(value) : value
                      }
                      interval={0}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                      width={80}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: number) => [formatCurrency(value), 'Cost']}
                      labelFormatter={(label) =>
                        reportConfig.groupBy === 'service' ? getServiceAbbreviation(label) : label
                      }
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="cost" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon="chart"
                  title="No data available"
                  description="Cost data will appear here once available."
                  className="h-64"
                />
              )}
            </div>
          )}

          {/* Data Table */}
          {(reportConfig.format === 'table' || reportConfig.format === 'both') && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {reportConfig.groupBy === 'service'
                    ? 'Services'
                    : reportConfig.groupBy === 'account'
                      ? 'Accounts'
                      : 'Regions'}{' '}
                  Breakdown
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportConfig.groupBy === 'service'
                          ? 'Service'
                          : reportConfig.groupBy === 'account'
                            ? 'Account'
                            : 'Region'}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {reportData.breakdown.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {reportConfig.groupBy === 'service'
                            ? item.service
                            : reportConfig.groupBy === 'account'
                              ? item.accountName || item.accountId
                              : item.region}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(item.cost)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {(Number(item.percentage) || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                    {reportData.breakdown.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
